import { useEffect, useState } from 'react';
import { getEligibleScenes, pickNextScene } from './frogScenes';

const INITIAL_DELAY = { min: 4000, max: 7000 };
const SCENE_GAP = { min: 9000, max: 18000 };

const randomDelay = ({ min, max }) =>
  Math.round(min + Math.random() * (max - min));

const getReducedMotionPreference = () =>
  typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export const useAmbientFrogScheduler = () => {
  const [activeScene, setActiveScene] = useState(null);
  const [reducedMotion, setReducedMotion] = useState(getReducedMotionPreference);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleChange = (event) => setReducedMotion(event.matches);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  useEffect(() => {
    if (reducedMotion) {
      setActiveScene(null);
      return undefined;
    }

    let timeoutId;
    let cancelled = false;
    let previousSceneId = null;

    const scheduleScene = (initial = false) => {
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => {
        if (cancelled || document.visibilityState === 'hidden') {
          return;
        }

        const scene = pickNextScene(getEligibleScenes(), previousSceneId);
        if (!scene) {
          scheduleScene(false);
          return;
        }

        previousSceneId = scene.id;
        setActiveScene(scene);
        timeoutId = window.setTimeout(() => {
          setActiveScene(null);
          scheduleScene(false);
        }, scene.duration);
      }, randomDelay(initial ? INITIAL_DELAY : SCENE_GAP));
    };

    const handleVisibilityChange = () => {
      window.clearTimeout(timeoutId);
      setActiveScene(null);

      if (document.visibilityState === 'visible') {
        scheduleScene(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    scheduleScene(true);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [reducedMotion]);

  return reducedMotion ? null : activeScene;
};
